import {
  FileText,
  Upload,
  Download,
  Shield,
  CheckCircle,
  LayoutGrid,
  List,
  Search,
  Trash2,
  Share2,
  Syringe,
  Award,
  Pill,
  Printer,
  Eye,
  X,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Pet } from '../types/pet';
import { apiClient } from '@/api/axios';
import { useNotifications } from '../context/NotificationsContext';
import { UploadDocumentModal } from './documents/UploadDocumentModal';
import { UploadChoiceModal } from './documents/UploadChoiceModal';
import { ScanDocumentModal } from './documents/ScanDocumentModal';
import { DocumentPreviewModal } from './documents/DocumentPreviewModal';
import { DashboardHeaderControls } from './DashboardHeaderControls';
import { ShareOptionsModal } from './shared/ShareOptionsModal';
import { QRCodeCanvas } from './shared/QRCodeCanvas';

const DOCUMENTS_VIEW_KEY = 'petso-documents-view';
type ViewMode = 'grid' | 'list';

const DOCUMENT_TYPE_OPTIONS = [
  { value: '', labelKey: 'documents.filterAll' },
  { value: 'vaccination-record', labelKey: 'documents.types.vaccination' },
  {
    value: 'health-certificate',
    labelKey: 'documents.types.healthCertificate',
  },
  { value: 'dna-report', labelKey: 'documents.types.dnaReport' },
  { value: 'ownership-record', labelKey: 'documents.types.ownershipRecord' },
  {
    value: 'microchip-certificate',
    labelKey: 'documents.types.microchipCertificate',
  },
  {
    value: 'pedigree-certificate',
    labelKey: 'documents.types.pedigreeCertificate',
  },
  { value: 'insurance-document', labelKey: 'documents.types.insurance' },
  { value: 'medical-record', labelKey: 'documents.types.medical' },
  { value: 'lab-result', labelKey: 'documents.types.labResult' },
  { value: 'x-ray-scan', labelKey: 'documents.types.xrayScan' },
  { value: 'surgical-report', labelKey: 'documents.types.surgicalReport' },
  { value: 'dental-record', labelKey: 'documents.types.dentalRecord' },
  { value: 'passport', labelKey: 'documents.types.passport' },
  { value: 'prescription', labelKey: 'documents.types.prescription' },
  { value: 'other', labelKey: 'documents.types.other' },
];

interface DocumentManagerProps {
  pet: Pet;
}

interface Document {
  id: string;
  name: string;
  displayName: string;
  fileName: string;
  type: string;
  date: string;
  hash: string;
  verified: boolean;
  issuer?: string;
  /** 'r2' = uploaded file, 'certificate' | 'prescription' | 'vaccination' = DB record */
  source: 'r2' | 'certificate' | 'prescription' | 'vaccination';
  /** Extra metadata for share/display */
  meta?: Record<string, unknown>;
}

/** Decode base64url (URL-safe base64, no padding) to UTF-8 string. */
function decodeBase64UrlToUtf8(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

function parseKeyForDisplay(key: string): {
  displayName: string;
  fileName: string;
} {
  const pathParts = key.split('/');
  const lastPart = pathParts[pathParts.length - 1] || '';
  // Key format when displayName is set: uid__base64url(displayName)__filename (filename may contain __)
  const segs = lastPart.split('__');
  if (segs.length >= 3) {
    try {
      const base64url = segs[1];
      const fileName = segs.slice(2).join('__');
      const displayName = decodeBase64UrlToUtf8(base64url);
      if (displayName && fileName) {
        return { displayName, fileName };
      }
    } catch {
      // fall through to legacy parsing
    }
  }
  const originalNameIdx = lastPart.indexOf('_');
  const fileName =
    originalNameIdx >= 0 ? lastPart.substring(originalNameIdx + 1) : lastPart;
  return { displayName: fileName, fileName };
}

/** User-facing title: prefer custom document name from upload; else filename (no ext); else empty for "Document". */
function cardDisplayName(displayName: string, fileName: string): string {
  if (displayName && displayName !== fileName) return displayName;
  if (fileName) {
    const withoutExt = fileName.replace(/\.[^/.]+$/, '').trim();
    return withoutExt || fileName;
  }
  return '';
}

function formatDocumentType(type: string): string {
  return type
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function DocumentManager({ pet }: DocumentManagerProps) {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(DOCUMENTS_VIEW_KEY);
      return stored === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [docToRemove, setDocToRemove] = useState<Document | null>(null);
  const [removing, setRemoving] = useState(false);
  const [shareDoc, setShareDoc] = useState<Document | null>(null);
  const [viewDoc, setViewDoc] = useState<Document | null>(null);
  const filteredDocuments =
    typeFilter === ''
      ? documents
      : documents.filter((d) => d.type === typeFilter);

  const persistViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(DOCUMENTS_VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  const getDocumentIcon = (doc: Document) => {
    if (doc.source === 'certificate') return <Award className="w-5 h-5" />;
    if (doc.source === 'prescription') return <Pill className="w-5 h-5" />;
    if (doc.source === 'vaccination') return <Syringe className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  const fetchDocuments = async () => {
    try {
      setError(null);
      setLoading(true);
      const res = await apiClient.get<{
        items: {
          key?: string | null;
          name?: string | null;
          size?: number | null;
          lastModified?: string | Date | null;
        }[];
        nextToken?: string;
      }>('/documents');
      const items = res.data.items || [];

      // Exclude profile/pet photos and Social uploads — only show documents.
      // Social uploads use keys/names like "social_..." or paths containing "social/" or "social_".
      const looksLikeSocial = (value: string): boolean => {
        const k = value.replace(/\\/g, '/').trim();
        const lower = k.toLowerCase();
        if (lower.startsWith('social_') || lower.includes('/social_'))
          return true;
        if (lower.includes('/social/')) return true;
        const parts = k.split('/');
        const lastSegment = (parts[parts.length - 1] || '').toLowerCase();
        if (lastSegment.startsWith('social_')) return true;
        // e.g. "prefix_social_1769805140300-uuid" from backend
        if (lastSegment.includes('_social_')) return true;
        return parts.some(
          (p) =>
            p.toLowerCase() === 'social' ||
            p.toLowerCase().startsWith('social_')
        );
      };
      const documentItems = items.filter((i) => {
        if (!i.key || typeof i.key !== 'string') return false;
        if (i.key.includes('/pets/')) return false;
        if (looksLikeSocial(i.key)) return false;
        if (i.name && typeof i.name === 'string' && looksLikeSocial(i.name))
          return false;
        return true;
      });
      const mapped: Document[] = documentItems
        .filter((i) => i.key)
        .map((i) => {
          const key = i.key as string;
          const pathParts = key.split('/');
          const type = pathParts.length > 1 ? pathParts[1] : 'other';
          const { displayName, fileName } = parseKeyForDisplay(key);
          const item = i as {
            key?: string;
            hash?: string;
            onChainHash?: string;
          };
          return {
            id: key,
            name: displayName,
            displayName,
            fileName,
            type,
            date: (i.lastModified
              ? new Date(i.lastModified as string)
              : new Date()
            )
              .toISOString()
              .split('T')[0],
            hash:
              typeof item.hash === 'string' && item.hash
                ? item.hash
                : typeof item.onChainHash === 'string' && item.onChainHash
                  ? item.onChainHash
                  : key,
            verified: false,
            issuer: undefined,
            source: 'r2',
          } as Document;
        });

      // Exclude any document that displays as social (backend key format may vary)
      const isSocialDisplay = (doc: Document): boolean => {
        const n = (doc.name || '').toLowerCase();
        const d = (doc.displayName || '').toLowerCase();
        return n.startsWith('social_') || d.startsWith('social_');
      };
      const filteredMapped = mapped.filter((doc) => !isSocialDisplay(doc));

      // ── Fetch system-generated documents (certificates, prescriptions, vaccinations) ──
      const systemDocs: Document[] = [];
      try {
        const [certsRes, rxRes, vacRes] = await Promise.allSettled([
          pet.id ? apiClient.get(`/pets/${pet.id}/certificates`) : Promise.resolve({ data: { certificates: [] } }),
          pet.id ? apiClient.get(`/pets/${pet.id}/prescriptions`) : Promise.resolve({ data: { prescriptions: [] } }),
          pet.id ? apiClient.get(`/pets/${pet.id}/vaccinations`) : Promise.resolve({ data: { vaccinations: [] } }),
        ]);

        // Certificates
        if (certsRes.status === 'fulfilled') {
          const certs = certsRes.value.data.certificates || [];
          for (const c of certs) {
            systemDocs.push({
              id: `cert-${c.id}`,
              name: c.title || `${formatDocumentType(c.certificateType || 'health-certificate')}`,
              displayName: c.title || `${formatDocumentType(c.certificateType || 'health-certificate')}`,
              fileName: `${c.certificateNumber || c.id}.pdf`,
              type: 'health-certificate',
              date: (c.issuedAt || c.createdAt || '').split('T')[0],
              hash: c.documentHash || c.id,
              verified: !!c.documentHash || !!c.txSignature,
              issuer: c.issuedByName || c.businessName,
              source: 'certificate',
              meta: c,
            });
          }
        }

        // Prescriptions
        if (rxRes.status === 'fulfilled') {
          const rxs = rxRes.value.data.prescriptions || [];
          for (const rx of rxs) {
            systemDocs.push({
              id: `rx-${rx.id}`,
              name: `Prescription — ${rx.diagnosis || rx.prescriptionNumber || 'Rx'}`,
              displayName: `Prescription — ${rx.diagnosis || rx.prescriptionNumber || 'Rx'}`,
              fileName: `${rx.prescriptionNumber || rx.id}.pdf`,
              type: 'medical-record',
              date: (rx.issuedAt || rx.createdAt || '').split('T')[0],
              hash: rx.id,
              verified: !!rx.signatureImageUrl,
              issuer: rx.issuedByName || rx.businessName,
              source: 'prescription',
              meta: rx,
            });
          }
        }

        // Vaccinations
        if (vacRes.status === 'fulfilled') {
          const vacs = vacRes.value.data.vaccinations || [];
          for (const v of vacs) {
            systemDocs.push({
              id: `vac-${v.id}`,
              name: `${v.vaccineName || 'Vaccination'}`,
              displayName: `${v.vaccineName || 'Vaccination'}`,
              fileName: `vaccination-${v.id}.pdf`,
              type: 'vaccination-record',
              date: (v.dateAdministered || v.createdAt || '').split('T')[0],
              hash: v.documentHash || v.id,
              verified: !!v.documentHash || !!v.txSignature || v.verified,
              issuer: v.administeredBy || v.clinicName,
              source: 'vaccination',
              meta: v,
            });
          }
        }
      } catch {
        // System docs are supplementary — don't block on failure
      }

      const allDocs = [...filteredMapped, ...systemDocs];
      allDocs.sort((a, b) =>
        a.date < b.date ? 1 : a.date > b.date ? -1 : 0
      );
      setDocuments(allDocs);
    } catch (e: unknown) {
      console.error('Failed to fetch documents', e);
      const err = e as {
        message?: string;
        response?: { status?: number; data?: { error?: string } };
      };
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.error;
      let message = 'Failed to load documents';
      if (status === 502 || status === 503) {
        message =
          serverMessage ||
          'Documents service is temporarily unavailable. Please try again in a moment.';
      } else if (serverMessage) {
        message = serverMessage;
      } else if (err?.message) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = (data?: { name?: string; type?: string }) => {
    if (data) {
      const docName = data.name?.trim() || 'Document';
      addNotification({
        type: 'document_shared',
        title: 'Document added',
        message: `"${docName}" was uploaded for ${pet.name}.`,
        petId: pet.id,
        petName: pet.name,
        icon: '📄',
        source: 'Documents',
      });
    }
    fetchDocuments();
    setShowUploadModal(false);
  };

  useEffect(() => {
    fetchDocuments();
  }, [pet.id]);

  const handleDownload = async (doc: Document) => {
    try {
      const encodedKey = encodeURIComponent(doc.id);
      const res = await apiClient.get(`/documents/${encodedKey}/download`, {
        responseType: 'blob',
        params: { disposition: 'attachment' },
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (e: unknown) {
      console.error('Download failed', e);
      setError(
        (e as { message?: string })?.message || 'Failed to download document'
      );
    }
  };

  const openPreview = (doc: Document) => {
    setPreviewDoc(doc);
  };

  const handleRemove = async () => {
    if (!docToRemove) return;
    setRemoving(true);
    try {
      const encodedKey = encodeURIComponent(docToRemove.id);
      await apiClient.delete(`/documents/${encodedKey}`);
      setDocToRemove(null);
      await fetchDocuments();
    } catch (e: unknown) {
      console.error('Remove failed', e);
      setError(
        (e as { message?: string })?.message || 'Failed to remove document'
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 min-w-0">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <h1
            className="text-2xl sm:text-3xl text-[#2D2F7E] dark:text-white min-w-0"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('documents.title')}
          </h1>
          <DashboardHeaderControls />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-lg border dark:border-white/8 overflow-hidden shrink-0">
              <button
                type="button"
                onClick={() => persistViewMode('grid')}
                className={`p-2.5 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
                aria-label={t('documents.gridView')}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => persistViewMode('list')}
                className={`p-2.5 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                    : 'bg-gray-100 dark:bg-[#0F0E16] text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/10'
                }`}
                aria-label={t('documents.listView')}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
            <label className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
              <span className="text-sm text-gray-600 dark:text-white/70 whitespace-nowrap shrink-0">
                {t('documents.filterByType')}:
              </span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="flex-1 min-w-0 sm:min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/8 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            type="button"
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity shrink-0"
            onClick={() => setShowChoiceModal(true)}
          >
            <Upload className="w-5 h-5" />
            {t('documents.upload')}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-4 mb-6 flex items-start gap-3 min-w-0">
        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="text-blue-900 dark:text-blue-300 font-medium">
            {t('documents.blockchainSecuredTitle')}
          </div>
          <div className="text-sm text-blue-700 dark:text-blue-400 break-words">
            {t('documents.blockchainSecuredDesc')}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded p-3 break-words">
          {error.includes('credentials') ||
          error.includes('Could not load credentials')
            ? t('documents.errorLoadCredentials')
            : error}
        </div>
      )}

      {viewMode === 'grid' && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {loading && (
            <div className="col-span-full text-center text-gray-500 dark:text-white/60 py-8">
              {t('common.loading')}
            </div>
          )}
          {!loading && filteredDocuments.length === 0 && (
            <div className="col-span-full text-center text-gray-500 dark:text-white/60 py-8">
              {typeFilter
                ? t('documents.noDocumentsForType')
                : t('documents.noDocuments')}
            </div>
          )}
          {!loading &&
            filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bg-white dark:bg-[#16151D] rounded-xl shadow-md hover:shadow-lg transition-shadow border dark:border-white/8 min-w-0 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white shrink-0 ${
                      doc.source === 'certificate' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                      doc.source === 'prescription' ? 'bg-gradient-to-br from-emerald-500 to-teal-600' :
                      doc.source === 'vaccination' ? 'bg-gradient-to-br from-rose-500 to-pink-600' :
                      'bg-gradient-to-br from-blue-500 to-purple-600'
                    }`}>
                      {getDocumentIcon(doc)}
                    </div>
                    <div className="flex items-center gap-1">
                      {doc.verified && (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          <span>{t('documents.verified')}</span>
                        </div>
                      )}
                      {doc.source !== 'r2' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                          {doc.source === 'certificate' ? 'Certificate' : doc.source === 'prescription' ? 'Prescription' : 'Vaccination'}
                        </span>
                      )}
                      {doc.source === 'r2' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDocToRemove(doc);
                          }}
                          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-500 hover:text-red-600 dark:text-white/50 dark:hover:text-red-400 transition-colors"
                          title={t('documents.removeDocument')}
                          aria-label={t('documents.removeDocument')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <h3 className="mb-1 dark:text-white font-semibold break-words">
                    {cardDisplayName(doc.displayName, doc.fileName) ||
                      t('documents.unnamedDocument')}
                  </h3>
                  <div className="text-sm text-gray-500 dark:text-white/50 mb-1">
                    {formatDocumentType(doc.type)}
                  </div>
                  {doc.issuer && (
                    <div className="text-xs text-gray-400 dark:text-white/40 mb-2">
                      {doc.issuer}
                    </div>
                  )}

                  <div className="text-sm text-gray-500 dark:text-white/50 mb-4">
                    {new Date(doc.date).toLocaleDateString()}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    {doc.source === 'r2' ? (
                      <>
                        <button
                          type="button"
                          className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                          onClick={() => openPreview(doc)}
                          title={t('documents.preview')}
                        >
                          <Search className="w-4 h-4" />
                          {t('documents.preview')}
                        </button>
                        <button
                          type="button"
                          className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                          onClick={() => handleDownload(doc)}
                          title={t('documents.download')}
                        >
                          <Download className="w-4 h-4" />
                          {t('documents.download')}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                        onClick={() => setViewDoc(doc)}
                        title={t('documents.view', 'View')}
                      >
                        <Eye className="w-4 h-4" />
                        {t('documents.view', 'View')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/5 px-3 py-2.5 sm:py-2 rounded-lg text-sm transition-colors dark:text-white"
                      onClick={() => setShareDoc(doc)}
                      title={t('shareModal.shareVia', 'Share')}
                    >
                      <Share2 className="w-4 h-4" />
                      {t('shareModal.shareVia', 'Share')}
                    </button>
                  </div>
                </div>
              </div>
            ))}

          {/* Add Document Card */}
          <div
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border-2 border-dashed border-gray-300 dark:border-white/8 hover:border-blue-400 dark:hover:border-blue-700/30 transition-colors cursor-pointer flex items-center justify-center min-h-[260px] sm:min-h-[300px] min-w-0"
            onClick={() => setShowChoiceModal(true)}
          >
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-[#0F0E16] rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-gray-400 dark:text-white/40" />
              </div>
              <div className="text-gray-600 dark:text-white/70 mb-2">
                {t('documents.uploadNew')}
              </div>
              <div className="text-sm text-gray-500 dark:text-white/50">
                {t('documents.uploadNewHint')}
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'list' && (
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-md border dark:border-white/8 overflow-hidden">
          {loading && (
            <div className="text-center text-gray-500 dark:text-white/60 py-8">
              {t('common.loading')}
            </div>
          )}
          {!loading && filteredDocuments.length === 0 && (
            <div className="text-center text-gray-500 dark:text-white/60 py-8">
              {typeFilter
                ? t('documents.noDocumentsForType')
                : t('documents.noDocuments')}
            </div>
          )}
          {!loading && filteredDocuments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
                    <th className="px-4 py-3 text-sm font-medium dark:text-white">
                      {t('documents.documentName')}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium dark:text-white">
                      {t('documents.type')}
                    </th>
                    <th className="px-4 py-3 text-sm font-medium dark:text-white w-28 text-right">
                      {t('documents.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      className="border-b dark:border-white/8 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="font-medium dark:text-white">
                            {cardDisplayName(doc.displayName, doc.fileName) ||
                              t('documents.unnamedDocument')}
                          </div>
                          {doc.source !== 'r2' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 shrink-0">
                              {doc.source === 'certificate' ? 'Certificate' : doc.source === 'prescription' ? 'Prescription' : 'Vaccination'}
                            </span>
                          )}
                        </div>
                        {doc.issuer && (
                          <div className="text-xs text-gray-400 dark:text-white/40">{doc.issuer}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-white/70">
                        {formatDocumentType(doc.type)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {doc.source === 'r2' ? (
                            <>
                              <button
                                type="button"
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                                onClick={() => openPreview(doc)}
                                title={t('documents.preview')}
                              >
                                <Search className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                                onClick={() => handleDownload(doc)}
                                title={t('documents.download')}
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                              onClick={() => setViewDoc(doc)}
                              title={t('documents.view', 'View')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors"
                            onClick={() => setShareDoc(doc)}
                            title={t('shareModal.shareVia', 'Share')}
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          {doc.source === 'r2' && (
                            <button
                              type="button"
                              className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-600 dark:text-white/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              onClick={() => setDocToRemove(doc)}
                              title={t('documents.removeDocument')}
                              aria-label={t('documents.removeDocument')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filteredDocuments.length > 0 && (
            <div
              className="border-t dark:border-white/8 p-4 flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              onClick={() => setShowChoiceModal(true)}
            >
              <Upload className="w-5 h-5 text-gray-500 dark:text-white/50" />
              <span className="text-sm text-gray-600 dark:text-white/70">
                {t('documents.uploadNew')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Document Types Info - 2x2 on mobile */}
      <div className="mt-6 sm:mt-8 bg-white dark:bg-[#16151D] rounded-xl shadow-md p-4 sm:p-6 border dark:border-white/8 min-w-0">
        <h3 className="text-lg sm:text-xl mb-4 dark:text-white">
          {t('documents.supportedTypes')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[
            {
              titleKey: 'documents.typeCardVaccinationTitle',
              descKey: 'documents.typeCardVaccinationDesc',
            },
            {
              titleKey: 'documents.typeCardHealthTitle',
              descKey: 'documents.typeCardHealthDesc',
            },
            {
              titleKey: 'documents.typeCardDnaTitle',
              descKey: 'documents.typeCardDnaDesc',
            },
            {
              titleKey: 'documents.typeCardOwnershipTitle',
              descKey: 'documents.typeCardOwnershipDesc',
            },
          ].map((item, index) => (
            <div
              key={index}
              className="p-4 bg-gray-50 dark:bg-[#0F0E16] rounded-lg min-w-0"
            >
              <div className="mb-2 dark:text-white font-medium">
                {t(item.titleKey)}
              </div>
              <div className="text-sm text-gray-600 dark:text-white/70 break-words">
                {t(item.descKey)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showChoiceModal && (
        <UploadChoiceModal
          onClose={() => setShowChoiceModal(false)}
          onChooseUpload={() => {
            setShowChoiceModal(false);
            setShowUploadModal(true);
          }}
          onChooseScan={() => {
            setShowChoiceModal(false);
            setShowScanModal(true);
          }}
        />
      )}

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
        />
      )}

      {showScanModal && (
        <ScanDocumentModal
          onClose={() => setShowScanModal(false)}
          onUpload={handleUpload}
        />
      )}

      {/* Remove confirm modal - outside view mode so it shows in both grid and list */}
      {docToRemove && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-xl max-w-md w-full p-6 border dark:border-white/8">
            <h3 className="text-lg font-semibold dark:text-white mb-2">
              {t('documents.removeDocument')}
            </h3>
            <p className="text-gray-600 dark:text-white/70 text-sm mb-6">
              {t('documents.removeConfirm')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setDocToRemove(null)}
                className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {removing ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDoc && (
        <DocumentPreviewModal
          document={{
            id: previewDoc.id,
            displayName:
              cardDisplayName(previewDoc.displayName, previewDoc.fileName) ||
              t('documents.unnamedDocument'),
            fileName: previewDoc.fileName,
            type: previewDoc.type,
          }}
          getPreviewUrl={async (key) => {
            const encodedKey = encodeURIComponent(key);
            const res = await apiClient.get(`/documents/${encodedKey}/preview`, {
              responseType: 'blob',
            });
            return URL.createObjectURL(res.data);
          }}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {/* Prescription signed view */}
      {viewDoc && viewDoc.source === 'prescription' && viewDoc.meta && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setViewDoc(null)}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 overflow-auto" id="rx-detail-content">
              {/* Re-use PrescriptionDetailView body inline — header */}
              {(() => {
                const rx = viewDoc.meta as Record<string, any>;
                const formatDate = (d: string | null | undefined) => {
                  if (!d) return '—';
                  try { return new Date(d).toLocaleDateString(); } catch { return d; }
                };
                return (
                  <>
                    {/* Header */}
                    <div className="p-6 border-b border-gray-200 dark:border-white/8 flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                          <Pill className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Prescription</h3>
                          <p className="text-sm text-gray-500 dark:text-white/50 font-mono mt-0.5">{rx.prescriptionNumber}</p>
                        </div>
                      </div>
                      <button onClick={() => setViewDoc(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-white/70" aria-label="Close">
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-5">
                      {/* Business branding */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-lg p-4 border border-blue-100 dark:border-blue-500/20">
                        <p className="text-xs text-blue-700 dark:text-blue-300 uppercase tracking-wider font-semibold">Issued By</p>
                        <p className="text-base font-bold text-gray-900 dark:text-white mt-0.5">{rx.businessName}</p>
                      </div>

                      {/* Pet & Owner */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500 dark:text-white/50">Pet</p>
                          <p className="text-gray-900 dark:text-white font-medium">{rx.petName}</p>
                          {(rx.petSpecies || rx.petBreed) && (
                            <p className="text-xs text-gray-500 dark:text-white/50">{rx.petSpecies}{rx.petBreed ? ` · ${rx.petBreed}` : ''}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-white/50">Owner</p>
                          <p className="text-gray-900 dark:text-white">{rx.ownerName || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-white/50">Issued</p>
                          <p className="text-gray-900 dark:text-white">{formatDate(rx.issuedAt)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 dark:text-white/50">Valid Until</p>
                          <p className="text-gray-900 dark:text-white">{formatDate(rx.validUntil)}</p>
                        </div>
                      </div>

                      {/* Diagnosis */}
                      {rx.diagnosis && (
                        <div className="text-sm">
                          <p className="text-gray-500 dark:text-white/50 mb-1">Diagnosis</p>
                          <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-white/5 p-3 rounded-lg">{rx.diagnosis}</p>
                        </div>
                      )}

                      {/* Medications */}
                      {Array.isArray(rx.medications) && rx.medications.length > 0 && (
                        <div>
                          <p className="text-gray-500 dark:text-white/50 text-sm mb-2">Medications ({rx.medications.length})</p>
                          <div className="space-y-2">
                            {rx.medications.map((med: any, idx: number) => (
                              <div key={idx} className="border border-gray-200 dark:border-white/8 rounded-lg p-3 bg-white dark:bg-white/5">
                                <div className="flex items-start gap-2">
                                  <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm text-gray-900 dark:text-white">{med.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-white/60 mt-0.5">
                                      {[med.dosage, med.frequency, med.duration, med.route].filter(Boolean).join(' · ')}
                                    </p>
                                    {med.instructions && <p className="text-xs text-gray-600 dark:text-white/70 mt-1 italic">{med.instructions}</p>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Instructions */}
                      {rx.instructions && (
                        <div className="text-sm">
                          <p className="text-gray-500 dark:text-white/50 mb-1">Dispensing Notes</p>
                          <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-white/5 p-3 rounded-lg">{rx.instructions}</p>
                        </div>
                      )}

                      {/* Remarks */}
                      {rx.remarks && (
                        <div className="text-sm">
                          <p className="text-gray-500 dark:text-white/50 mb-1">Remarks</p>
                          <p className="text-gray-900 dark:text-white">{rx.remarks}</p>
                        </div>
                      )}

                      {/* Signature Footer */}
                      <div className="mt-6 pt-5 border-t border-gray-200 dark:border-white/10">
                        {rx.signatureImageUrl ? (
                          <div className="flex flex-col items-center">
                            <img src={rx.signatureImageUrl} alt="Issuer signature" style={{ maxHeight: 80, maxWidth: '100%', background: '#fff', borderRadius: 8, padding: 4 }} />
                            <div className="w-48 border-t border-gray-400 mt-1" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white mt-2">{rx.signerName || rx.issuedByName || '—'}</p>
                            {(rx.issuedByTitle || rx.issuedByLicenseNo) && (
                              <p className="text-xs text-gray-500 dark:text-white/60">
                                {rx.issuedByTitle || ''}{rx.issuedByTitle && rx.issuedByLicenseNo ? ' · ' : ''}{rx.issuedByLicenseNo ? `License ${rx.issuedByLicenseNo}` : ''}
                              </p>
                            )}
                            {rx.signedAt && <p className="text-[11px] text-gray-400 dark:text-white/40 mt-1">Signed on {formatDate(rx.signedAt)}</p>}
                          </div>
                        ) : (
                          <div className="text-center text-xs text-gray-400 dark:text-white/40 italic">Unsigned prescription</div>
                        )}
                      </div>

                      {/* Revoked banner */}
                      {rx.status === 'revoked' && (
                        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3 text-sm flex items-start gap-2">
                          <div>
                            <p className="text-red-700 dark:text-red-400 font-medium">This prescription has been revoked</p>
                            {rx.revokedReason && <p className="text-red-600/80 dark:text-red-400/80 text-xs mt-0.5">{rx.revokedReason}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 p-4 border-t dark:border-white/8 shrink-0">
              <button
                type="button"
                onClick={() => {
                  const meta = viewDoc.meta || {};
                  const json = JSON.stringify(meta, null, 2);
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${viewDoc.fileName.replace(/\.\w+$/, '')}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('rx-detail-content');
                  if (el) {
                    const w = window.open('', '_blank');
                    if (w) {
                      w.document.write(`<html><head><title>Prescription</title><style>body{font-family:system-ui;padding:2rem;color:#333;max-width:700px;margin:0 auto}</style></head><body>`);
                      w.document.write(el.innerHTML);
                      w.document.write('</body></html>');
                      w.document.close();
                      w.print();
                    }
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                type="button"
                onClick={() => setShareDoc(viewDoc)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              <button
                type="button"
                onClick={() => setViewDoc(null)}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate signed view */}
      {viewDoc && viewDoc.source === 'certificate' && viewDoc.meta && (() => {
        const cert = viewDoc.meta as Record<string, any>;
        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem',
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setViewDoc(null);
            }}
          >
            <div
              className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl"
              style={{
                maxWidth: '28rem',
                width: '100%',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(to right, #059669, #047857)',
                  color: 'white',
                  padding: '1.5rem',
                  borderRadius: '1rem 1rem 0 0',
                  flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShieldCheck style={{ width: '20px', height: '20px' }} />
                    {cert.title || 'Vaccination Certificate'}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setViewDoc(null)}
                    style={{ color: 'rgba(255,255,255,0.8)', cursor: 'pointer', background: 'none', border: 'none' }}
                  >
                    <X style={{ width: '20px', height: '20px' }} />
                  </button>
                </div>
                <p style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '4px' }}>{cert.certificateNumber}</p>
              </div>
              <div data-cert-print style={{ padding: '1.5rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div style={{ width: '100%', background: '#fff', borderRadius: '1rem', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                  <div style={{ background: '#0f172a', padding: '16px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase' }}>Issued by</p>
                      <p style={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                        {cert.issuedByTitle ? `${cert.issuedByTitle} ` : ''}
                        {cert.issuedByName || 'Professional'}
                      </p>
                      {cert.businessName && (
                        <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>{cert.businessName}</p>
                      )}
                    </div>
                    <ShieldCheck style={{ color: '#34d399', width: '22px', height: '22px' }} />
                  </div>
                  <div style={{ background: '#f8fafc', padding: '10px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Issued To</p>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{cert.petName}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Document</p>
                      <p style={{ fontSize: '11px', fontWeight: 500, color: '#0f172a' }}>{cert.title}</p>
                    </div>
                  </div>
                  {cert.data && (() => {
                    const d =
                      typeof cert.data === 'string'
                        ? (() => {
                            try {
                              return JSON.parse(cert.data);
                            } catch {
                              return {};
                            }
                          })()
                        : cert.data;
                    return (
                      <div style={{ padding: '12px 16px' }}>
                        {Object.entries(d)
                          .filter(
                            ([k, v]) =>
                              v &&
                              ![
                                'certificateId',
                                'certificateNumber',
                                'certificateType',
                                'title',
                                'issuedAt',
                                'issuedByName',
                                'issuedByTitle',
                                'documentHash',
                                'systems',
                              ].includes(k),
                          )
                          .map(([key, value]) => (
                            <div
                              key={key}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '11px',
                                borderBottom: '1px solid #f1f5f9',
                                padding: '4px 0',
                              }}
                            >
                              <span style={{ color: '#64748b', textTransform: 'capitalize' }}>
                                {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                              </span>
                              <span style={{ fontWeight: 500, color: '#1e293b' }}>{String(value)}</span>
                            </div>
                          ))}
                      </div>
                    );
                  })()}
                  {cert.remarks && (
                    <div style={{ padding: '8px 16px', fontSize: '11px', color: '#64748b', borderTop: '1px solid #f1f5f9' }}>
                      <span style={{ fontWeight: 600 }}>Remarks:</span> {cert.remarks}
                    </div>
                  )}
                  <div style={{ padding: '8px 16px', borderTop: '1px solid #e2e8f0', fontSize: '11px' }}>
                    {cert.certificateNumber && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ color: '#64748b' }}>Certificate No.</span>
                        <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace', fontSize: '10px' }}>
                          {cert.certificateNumber}
                        </span>
                      </div>
                    )}
                    {cert.issuedAt && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ color: '#64748b' }}>Issued</span>
                        <span style={{ fontWeight: 500, color: '#0f172a' }}>
                          {new Date(cert.issuedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {cert.validFrom && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ color: '#64748b' }}>Valid From</span>
                        <span style={{ fontWeight: 500, color: '#0f172a' }}>{cert.validFrom}</span>
                      </div>
                    )}
                    {cert.validUntil && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                        <span style={{ color: '#64748b' }}>Valid Until</span>
                        <span style={{ fontWeight: 500, color: '#0f172a' }}>{cert.validUntil}</span>
                      </div>
                    )}
                  </div>
                  {cert.signatureImageUrl && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img
                        src={cert.signatureImageUrl}
                        alt="Issuer signature"
                        style={{ maxHeight: 60, maxWidth: '100%', background: '#fff', borderRadius: 6, padding: 2 }}
                      />
                      <div style={{ width: '10rem', borderTop: '1px solid #94a3b8', marginTop: '4px' }} />
                      <p style={{ fontSize: '11px', fontWeight: 600, color: '#0f172a', marginTop: '4px' }}>
                        {cert.signerName || cert.issuedByName || ''}
                      </p>
                      {cert.signedAt && (
                        <p style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>
                          Signed {new Date(cert.signedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      background: '#ecfdf5',
                      padding: '12px 16px',
                      borderTop: '1px solid #a7f3d0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: '8px',
                          fontWeight: 700,
                          color: '#065f46',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <ShieldCheck style={{ width: '10px', height: '10px' }} /> Cryptographically Verified
                      </p>
                      <p
                        style={{
                          fontSize: '7px',
                          color: '#047857',
                          fontFamily: 'monospace',
                          marginTop: '4px',
                          wordBreak: 'break-all',
                        }}
                      >
                        {cert.documentHash
                          ? String(cert.documentHash).slice(0, 32) + '...'
                          : cert.certificateNumber}
                      </p>
                    </div>
                    <div style={{ background: '#fff', padding: '4px', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                      <QRCodeCanvas
                        value={
                          cert.verificationUrl ||
                          `https://api-dev.petso.dev/verify/${cert.certificateNumber}`
                        }
                        size={48}
                      />
                    </div>
                  </div>
                </div>
                {cert.verificationUrl && (
                  <a
                    href={cert.verificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mt-3"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Verify certificate publicly
                  </a>
                )}
              </div>
              <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    const meta = viewDoc.meta || {};
                    const json = JSON.stringify(meta, null, 2);
                    const blob = new Blob([json], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${viewDoc.fileName.replace(/\.\w+$/, '')}.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#f3f4f6', fontSize: '13px', border: 'none', cursor: 'pointer' }}
                >
                  <Download style={{ width: '14px', height: '14px' }} />
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const w = window.open('', '_blank');
                    if (w) {
                      const el = document.querySelector('[data-cert-print]');
                      w.document.write(`<html><head><title>${cert.title || 'Certificate'}</title><style>body{font-family:system-ui;margin:0;padding:2rem}</style></head><body>`);
                      if (el) w.document.write(el.innerHTML);
                      w.document.write('</body></html>');
                      w.document.close();
                      w.print();
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#f3f4f6', fontSize: '13px', border: 'none', cursor: 'pointer' }}
                >
                  <Printer style={{ width: '14px', height: '14px' }} />
                  Print
                </button>
                <button
                  type="button"
                  onClick={() => setShareDoc(viewDoc)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#f3f4f6', fontSize: '13px', border: 'none', cursor: 'pointer' }}
                >
                  <Share2 style={{ width: '14px', height: '14px' }} />
                  Share
                </button>
                <button
                  type="button"
                  onClick={() => setViewDoc(null)}
                  style={{ padding: '8px 16px', backgroundColor: '#059669', color: '#fff', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Vaccination detail modal */}
      {viewDoc && viewDoc.source === 'vaccination' && viewDoc.meta && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border dark:border-white/8">
            <div className="flex items-start justify-between p-6 border-b dark:border-white/8">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 bg-gradient-to-br from-rose-500 to-pink-600">
                  <Syringe className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold dark:text-white truncate">
                    {cardDisplayName(viewDoc.displayName, viewDoc.fileName) || t('documents.unnamedDocument')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-white/50">Vaccination Record</p>
                </div>
              </div>
              <button type="button" onClick={() => setViewDoc(null)} className="p-1 text-gray-400 hover:text-gray-600 dark:text-white/50 dark:hover:text-white/80">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div id="doc-detail-content" className="p-6 space-y-4">
              {viewDoc.verified && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  {t('documents.blockchainVerified', 'Blockchain Verified')}
                </div>
              )}
              {(() => {
                const m = viewDoc.meta as Record<string, any>;
                return (
                  <div className="space-y-3">
                    {m.vaccineName && <DetailRow label={t('documents.vaccine', 'Vaccine')} value={m.vaccineName} />}
                    {m.dateAdministered && <DetailRow label={t('documents.dateAdministered', 'Date Administered')} value={new Date(m.dateAdministered).toLocaleDateString()} />}
                    {m.expiryDate && <DetailRow label={t('documents.expiryDate', 'Expiry Date')} value={new Date(m.expiryDate).toLocaleDateString()} />}
                    {m.boosterDate && <DetailRow label={t('documents.boosterDate', 'Booster Date')} value={new Date(m.boosterDate).toLocaleDateString()} />}
                    {m.administeredBy && <DetailRow label={t('documents.administeredBy', 'Administered By')} value={m.administeredBy} />}
                    {m.clinicName && <DetailRow label={t('documents.clinic', 'Clinic')} value={m.clinicName} />}
                    {m.batchNumber && <DetailRow label={t('documents.batchNumber', 'Batch #')} value={m.batchNumber} />}
                    {m.notes && <DetailRow label={t('documents.notes', 'Notes')} value={m.notes} />}
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center justify-end gap-2 p-6 border-t dark:border-white/8">
              <button
                type="button"
                onClick={() => {
                  const meta = viewDoc.meta || {};
                  const json = JSON.stringify(meta, null, 2);
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${viewDoc.fileName.replace(/\.\w+$/, '')}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('documents.download', 'Download')}
              </button>
              <button
                type="button"
                onClick={() => {
                  const detailEl = document.getElementById('doc-detail-content');
                  if (detailEl) {
                    const w = window.open('', '_blank');
                    if (w) {
                      w.document.write(`<html><head><title>${cardDisplayName(viewDoc.displayName, viewDoc.fileName)}</title><style>body{font-family:system-ui;padding:2rem;color:#333;max-width:700px;margin:0 auto}</style></head><body>`);
                      w.document.write(`<h2>${cardDisplayName(viewDoc.displayName, viewDoc.fileName)}</h2>`);
                      w.document.write(`<p>${pet.name} — Vaccination Record</p>`);
                      w.document.write(detailEl.innerHTML);
                      w.document.write('</body></html>');
                      w.document.close();
                      w.print();
                    }
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Printer className="w-4 h-4" />
                {t('documents.print', 'Print')}
              </button>
              <button
                type="button"
                onClick={() => setShareDoc(viewDoc)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-[#0F0E16] hover:bg-gray-200 dark:hover:bg-white/10 text-sm dark:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {t('shareModal.shareVia', 'Share')}
              </button>
              <button
                type="button"
                onClick={() => setViewDoc(null)}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm transition-colors"
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ShareOptionsModal
        isOpen={!!shareDoc}
        onClose={() => setShareDoc(null)}
        title={shareDoc ? cardDisplayName(shareDoc.displayName, shareDoc.fileName) || 'Document' : 'Document'}
        message={shareDoc ? `${pet.name}'s ${formatDocumentType(shareDoc.type)}${shareDoc.issuer ? ` — issued by ${shareDoc.issuer}` : ''}` : ''}
        shareType="document"
        fileName={shareDoc?.fileName}
      />
    </div>
  );
}

/** Small helper for detail rows in the view modal */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <div className="text-xs font-medium text-gray-500 dark:text-white/50 min-w-[120px] shrink-0">{label}</div>
      <div className="text-sm text-gray-900 dark:text-white break-words">{value}</div>
    </div>
  );
}
